import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import QueryString from "qs";
import { StrapiProductTag, StrapiResponse } from "~/shared-types";
import { strapiClient as defaultStrapiClient } from "~/strapi/api";
import {
  EMPTY_STRAPI_RESPONSE,
  QueryFunctionOptions,
  getNextPageParam,
  getQueryObject,
} from "./shared";
import { handleError } from "~/utils/rollbar";

type ProductTagsOptions = QueryFunctionOptions & {
  filters?: QueryFunctionOptions["filters"] & {
    store?: number;
  };
};

export const PRODUCT_TAGS_QUERY_KEY = "productTags";

export async function getProductTags(options: QueryFunctionOptions = {}) {
  const { strapiClient = defaultStrapiClient } = options;

  if (!options?.filters?.store) return EMPTY_STRAPI_RESPONSE;

  const queryObject = getQueryObject(options);
  const query = QueryString.stringify({
    ...queryObject,
    populate: ["products", "store"],
  });
  const { data } = await strapiClient.get<StrapiResponse<StrapiProductTag[]>>(
    `/product-tags?${query}`
  );

  return data;
}

export const useProductTagsQuery = (
  options: ProductTagsOptions,
  initialData?: StrapiResponse<StrapiProductTag[]>
) => {
  const queryObject = getQueryObject(options);
  const query = QueryString.stringify(queryObject);

  const productTags = useInfiniteQuery({
    queryKey: [PRODUCT_TAGS_QUERY_KEY, query],
    queryFn: async ({ pageParam = 1 }) =>
      getProductTags({ ...options, pageParam }),
    getNextPageParam,
    initialPageParam: initialData ? 1 : undefined,
    initialData: {
      pages: [initialData],
      pageParams: [initialData ? 1 : undefined],
    },
  });
  return productTags;
};

export const useAddProductTag = (options: ProductTagsOptions) => {
  const { strapiClient = defaultStrapiClient } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: { title: string; productId?: number }) => {
      const { data } = await strapiClient.post<
        StrapiResponse<StrapiProductTag>
      >(`/product-tags`, {
        data: body,
      });

      return data.data;
    },
    onSuccess: (newProductTag: StrapiProductTag) => {
      const queryObject = getQueryObject(options);
      const query = QueryString.stringify(queryObject);

      queryClient.setQueryData<
        InfiniteData<StrapiResponse<StrapiProductTag[]> | undefined>
      >([PRODUCT_TAGS_QUERY_KEY, query], (previousData) => {
        if (!previousData) {
          return {
            pages: [{ data: [newProductTag], meta: {} }],
            pageParams: [undefined],
          };
        }

        // Add the new tag to the first page of results
        const firstPage = previousData.pages[0];
        return {
          ...previousData,
          pages: [
            {
              ...firstPage,
              meta: { ...firstPage?.meta },
              data: [newProductTag, ...(firstPage?.data ?? [])],
            },
            ...previousData.pages.slice(1),
          ],
        };
      });
    },
    onError: handleError,
  });
};
